import type { ConnectionStatus } from '../net/use-game';

/**
 * Connection-status indicator (NFR-4.2 auto-reconnect visibility).
 *
 * Distinguishes the real lifecycle states rather than a single boolean — a host who
 * hasn't created a game yet has NO socket open, so labeling that "Reconnecting…" is
 * wrong (and confusing: nothing is being reconnected). States:
 *   - `idle`         → no connection intended yet (e.g. pre-create host lobby) —
 *                      renders NOTHING (there is nothing to be connected to yet);
 *   - `connecting`   → first socket open in progress;
 *   - `reconnecting` → was connected, dropped, retrying (the true NFR-4.2 case);
 *   - `connected`    → open.
 */
const LABEL: Record<Exclude<ConnectionStatus, 'idle'>, string> = {
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  connected: 'Connected',
};

export function ConnectionBadge({
  status,
}: {
  status: ConnectionStatus;
}): React.JSX.Element | null {
  // Idle = no game to connect to yet (host pre-create). Show no badge at all — a
  // connection indicator is meaningless before there's a connection to have.
  if (status === 'idle') return null;
  return (
    <span
      className={'conn-dot' + (status === 'connected' ? ' conn-dot--on' : '')}
      data-testid="conn-status"
      data-status={status}
    >
      {LABEL[status]}
    </span>
  );
}
